import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, BadgeCheck, LogIn, KeyRound } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { openSignIn } from "@/lib/signInModal";
import { DriverMyListingSection } from "@/components/drivers/DriverMyListingSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  offer_sent: "Awaiting Your Response",
  driver_accepted: "You Accepted",
  declined: "You Declined",
  responsible_motor_carrier_confirmed: "Carrier Confirmed",
  carrier_authorization_complete: "Carrier Authorized",
  driver_clearance_complete: "Clearance Complete",
  ready_to_operate: "Ready to Operate",
};

const DriverPortalGate = () => (
  <div data-testid="driver-portal-gate" className="min-h-screen bg-rr-navy flex items-center justify-center px-5 relative overflow-hidden">
    <div className="grain-overlay" />
    <div className="max-w-md text-center relative">
      <div className="w-14 h-14 rounded-2xl bg-rr-orange/10 text-rr-orangeLight flex items-center justify-center mx-auto mb-5">
        <LogIn size={24} />
      </div>
      <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-white">Driver Portal</h1>
      <p className="text-white/60 mt-3 leading-relaxed">Sign in with your RelayRigs driver account to view broker opportunities and join private networks.</p>
      <button onClick={() => openSignIn("signin")} data-testid="driver-portal-gate-signin-button" className="mt-8 inline-flex items-center gap-2 bg-rr-orange text-white font-bold px-7 py-3.5 rounded-full hover:bg-white hover:text-rr-navy transition-all">
        Sign In
      </button>
    </div>
  </div>
);

export default function DriverPortal() {
  const user = useAuth();
  const [offers, setOffers] = useState(null);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [respondingId, setRespondingId] = useState(null);

  const loadOffers = useCallback(() => axios.get(`${API}/driver/offers`).then(({ data }) => setOffers(data.offers)).catch(() => setOffers([])), []);

  useEffect(() => { if (user) loadOffers(); }, [user, loadOffers]);

  if (!user) {
    return (
      <>
        <Header />
        <DriverPortalGate />
      </>
    );
  }

  const joinNetwork = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      await axios.post(`${API}/driver/join-network`, { code });
      toast.success("You've joined that broker's network!");
      setCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't join with that code.");
    } finally {
      setJoining(false);
    }
  };

  const respond = async (id, status) => {
    setRespondingId(id);
    try {
      await axios.patch(`${API}/driver/offers/${id}`, { status });
      toast.success(status === "driver_accepted" ? "Opportunity accepted!" : "Opportunity declined.");
      loadOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't respond to this offer.");
    } finally {
      setRespondingId(null);
    }
  };

  return (
    <div data-testid="driver-portal-page" className="min-h-screen bg-rr-surface">
      <Header />
      <div className="pt-32 pb-20 max-w-4xl mx-auto px-5 md:px-8">
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-rr-navy">Driver Portal</h1>
        <p className="text-rr-muted text-sm mt-1 mb-8">View broker-sent opportunities and join private driver networks.</p>

        <div className="bg-white border border-rr-line rounded-2xl p-6 mb-10">
          <h2 className="font-heading text-lg font-bold text-rr-navy mb-3 flex items-center gap-2"><KeyRound size={17} className="text-rr-orange" /> Join a Broker Network</h2>
          <form onSubmit={joinNetwork} data-testid="driver-join-network-form" className="flex flex-wrap gap-2">
            <input
              required
              data-testid="driver-join-network-code-input"
              placeholder="Enter network join code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="flex-1 min-w-[200px] border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange uppercase"
            />
            <button type="submit" disabled={joining} data-testid="driver-join-network-submit-button" className="bg-rr-orange text-white font-bold px-6 py-3 rounded-full hover:bg-rr-navy transition-colors flex items-center gap-2 disabled:opacity-60">
              {joining ? <Loader2 size={16} className="animate-spin" /> : "Join Network"}
            </button>
          </form>
          <p className="text-xs text-rr-muted mt-3">Don't have a Driver Directory profile yet? <a href="/drivers" className="text-rr-orange font-bold hover:underline">Create one here</a> first.</p>
        </div>

        <div className="mb-10">
          <h2 className="font-heading text-lg font-bold text-rr-navy mb-3">My Directory Listing</h2>
          <DriverMyListingSection />
        </div>

        <h2 className="font-heading text-lg font-bold text-rr-navy mb-4">My Broker Opportunities</h2>
        {offers === null ? (
          <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-rr-orange" /></div>
        ) : offers.length === 0 ? (
          <p data-testid="driver-offers-empty-state" className="text-rr-muted text-sm">No broker opportunities yet — join a network above to start receiving them.</p>
        ) : (
          <div className="space-y-3">
            {offers.map((o) => (
              <div key={o.id} data-testid={`driver-offer-row-${o.id}`} className="bg-white border border-rr-line rounded-xl p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-heading font-bold text-rr-navy">{o.origin} → {o.destination}</p>
                      {o.broker_verified && (
                        <span data-testid={`driver-offer-verified-badge-${o.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-rr-green/10 text-rr-green px-2 py-0.5 rounded-full">
                          <ShieldCheck size={11} /> Verified Brokerage
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-rr-muted mt-1">
                      {o.equipment && `${o.equipment} · `}{o.driver_compensation ? `$${o.driver_compensation} driver pay` : ""}{o.responsible_motor_carrier && ` · ${o.responsible_motor_carrier}`}
                    </p>
                  </div>
                  <span data-testid={`driver-offer-status-${o.id}`} className="text-xs font-bold px-2.5 py-1 rounded-full bg-rr-navy/5 text-rr-navy shrink-0">
                    {STATUS_LABELS[o.status] || o.status}
                  </span>
                </div>
                {o.instructions && <p className="text-xs text-rr-muted mt-3">{o.instructions}</p>}
                {o.status === "offer_sent" && (
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => respond(o.id, "driver_accepted")}
                      disabled={respondingId === o.id}
                      data-testid={`driver-offer-accept-button-${o.id}`}
                      className="flex items-center gap-1.5 text-xs font-bold bg-rr-orange text-white px-4 py-2 rounded-full hover:bg-rr-navy transition-colors disabled:opacity-60"
                    >
                      <BadgeCheck size={13} /> Accept
                    </button>
                    <button
                      onClick={() => respond(o.id, "declined")}
                      disabled={respondingId === o.id}
                      data-testid={`driver-offer-decline-button-${o.id}`}
                      className="text-xs font-bold border border-rr-line text-rr-muted px-4 py-2 rounded-full hover:border-rr-red hover:text-rr-red transition-colors disabled:opacity-60"
                    >
                      Decline
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
